import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';
import {
  supportedLanguages,
  languageNames,
  addLanguagePrefix,
  removeLanguagePrefix,
  type SupportedLanguage
} from '@/lib/i18n';

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'toggle' | 'minimal';
  className?: string;
}

const LanguageSwitcher = ({ variant = 'toggle', className = '' }: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = i18n.language as SupportedLanguage;

  const switchLanguage = (newLang: SupportedLanguage) => {
    if (newLang === currentLang) return;

    // Change i18n language
    i18n.changeLanguage(newLang);

    // Navigate to the new language path
    const cleanPath = removeLanguagePrefix(location.pathname);
    const newPath = addLanguagePrefix(cleanPath, newLang);
    navigate(newPath + location.search + location.hash);
  };

  // Toggle variant - simple EN/ES switch
  if (variant === 'toggle') {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="flex items-center bg-accent rounded-full p-0.5">
          {supportedLanguages.map((lang) => (
            <button
              key={lang}
              onClick={() => switchLanguage(lang)}
              // Visual pill stays 24px tall; the ::before pad lifts the hit area to 44x44.
              className={`relative min-w-[44px] px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-200 before:absolute before:inset-x-0 before:-inset-y-2.5 before:content-[''] ${
                currentLang === lang
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:text-charcoal'
              }`}
              aria-label={`Switch to ${languageNames[lang]}`}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Minimal variant - just shows current language, clicking toggles
  if (variant === 'minimal') {
    const nextLang = currentLang === 'en' ? 'es' : 'en';
    return (
      <button
        onClick={() => switchLanguage(nextLang)}
        className={`flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors ${className}`}
        aria-label={`Switch to ${languageNames[nextLang]}`}
      >
        <Globe className="w-4 h-4" />
        <span>{currentLang.toUpperCase()}</span>
      </button>
    );
  }

  // Dropdown variant (for future use)
  return (
    <div className={`relative ${className}`}>
      <button
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-charcoal hover:text-primary transition-colors"
        aria-label="Select language"
      >
        <Globe className="w-4 h-4" />
        <span>{languageNames[currentLang]}</span>
      </button>
    </div>
  );
};

export default LanguageSwitcher;
